import { supabase, isConfigured } from './supabase';
import type {
  TaskTemplate,
  Enrolment,
  TaskTemplateStep,
} from '../types';

// ============================================================
// TASK TEMPLATES (Sequences)
// ============================================================

export async function getTaskTemplates(teamId: string): Promise<TaskTemplate[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_task_templates')
    .select('*')
    .eq('team_id', teamId)
    .order('is_default', { ascending: false })
    .order('name');

  if (error) {
    console.error('[sequences] getTaskTemplates failed:', error);
    return [];
  }

  return data as TaskTemplate[];
}

export async function createTaskTemplate(
  teamId: string,
  name: string,
  description: string | null,
  steps: TaskTemplateStep[]
): Promise<TaskTemplate | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_task_templates')
    .insert({
      team_id: teamId,
      name,
      description,
      steps,
    })
    .select()
    .single();

  if (error) {
    console.error('[sequences] createTaskTemplate failed:', error);
    return null;
  }

  return data as TaskTemplate;
}

export async function updateTaskTemplate(
  id: string,
  updates: Partial<Pick<TaskTemplate, 'name' | 'description' | 'steps'>>
): Promise<TaskTemplate | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_task_templates')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[sequences] updateTaskTemplate failed:', error);
    return null;
  }

  return data as TaskTemplate;
}

export async function deleteTaskTemplate(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_task_templates')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[sequences] deleteTaskTemplate failed:', error);
    return false;
  }

  return true;
}

// ============================================================
// ENROLMENTS
// ============================================================

export async function getEnrolmentsForContact(contactId: string): Promise<Enrolment[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_enrolments')
    .select(`
      *,
      template:template_id (id, name, steps)
    `)
    .eq('contact_id', contactId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[sequences] getEnrolmentsForContact failed:', error);
    return [];
  }

  return data as Enrolment[];
}

export async function enrolContactInSequence(
  teamId: string,
  contactId: string,
  templateId: string
): Promise<string | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase.rpc('enrol_contact_in_sequence', {
    p_team_id: teamId,
    p_contact_id: contactId,
    p_template_id: templateId,
  });

  if (error) {
    console.error('[sequences] enrolContactInSequence failed:', error);
    return null;
  }

  return data as string;
}

export async function cancelEnrolment(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_enrolments')
    .update({ status: 'cancelled' })
    .eq('id', id);

  if (error) {
    console.error('[sequences] cancelEnrolment failed:', error);
    return false;
  }

  return true;
}

// ============================================================
// SEED DEFAULT TEMPLATE
// ============================================================

export async function seedDefaultTemplate(teamId: string): Promise<void> {
  if (!isConfigured) return;

  const { error } = await supabase.rpc('seed_default_task_template', {
    p_team_id: teamId,
  });

  if (error) {
    console.error('[sequences] seedDefaultTemplate failed:', error);
  }
}
